import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getSocket } from '@/lib/websocket';
import { toast } from 'sonner';

/**
 * Listens for WebSocket events from other users and:
 * 1. Invalidates TanStack Query caches so data refreshes
 * 2. Shows toast notifications for changes by other users (conflict awareness)
 */
export function useRealtimeSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const socket = getSocket();

    // Get current user ID to distinguish own vs other user changes
    const currentUserId = socket.auth && typeof socket.auth === 'object'
      ? (socket.auth as Record<string, unknown>).userId
      : undefined;

    const handlers: Record<string, (data: any) => void> = {
      task_created: (data) => {
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        if (data.userId && data.userId !== currentUserId) {
          toast.info(`${data.userName || 'Someone'} created a new task`, { duration: 3000 });
        }
      },
      task_updated: (data) => {
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        if (data.userId && data.userId !== currentUserId) {
          toast.info(`${data.userName || 'Someone'} updated a task`, { duration: 3000 });
        }
      },
      task_deleted: (data) => {
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        if (data.userId && data.userId !== currentUserId) {
          toast.info(`${data.userName || 'Someone'} deleted a task`, { duration: 3000 });
        }
      },
      dependency_created: () =>
        queryClient.invalidateQueries({ queryKey: ['dependencies'] }),
      dependency_deleted: () =>
        queryClient.invalidateQueries({ queryKey: ['dependencies'] }),
      schedule_recalculated: (data) => {
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['projects', data.projectId] });
        toast.info('Schedule recalculated', { duration: 2000 });
      },
      cell_locked: (data) => {
        if (data.userId && data.userId !== currentUserId) {
          toast.info(`${data.userName || 'Someone'} is editing a cell`, { duration: 2000 });
        }
      },
    };

    Object.entries(handlers).forEach(([event, handler]) => socket.on(event, handler));

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => socket.off(event, handler));
    };
  }, [queryClient]);
}
